<template>
  <div>
    <Navigation />
    <div class="wrap">
      <h1>Settings</h1>
      <p class="lede">Business, users and integrations.</p>

      <div class="card">
        <h3>Business</h3>
        <div class="kv"><span>Tenant ID</span><strong>{{ tenantId }}</strong></div>
        <div class="kv"><span>Business name</span><strong>{{ tenantName }}</strong></div>
        <div class="kv"><span>Signed in as</span><strong>{{ userName }} <span :class="'badge '+userRole" style="margin-left:6px">{{ userRole }}</span></strong></div>
      </div>

      <div class="card">
        <h3>Cash session</h3>
        <div v-if="!openSession">
          <p class="muted">You have no cash drawer open.</p>
          <label>Opening float (KES)</label>
          <input type="number" v-model.number="openingFloat" style="max-width:200px" />
          <button class="primary" @click="openTill" :disabled="opening" style="margin-left:8px">
            {{ opening ? 'Opening…' : 'Open cash session' }}
          </button>
        </div>
        <div v-else>
          <div class="kv"><span>Session #</span><strong>{{ openSession.id }} at {{ openSession.shop_name }}</strong></div>
          <div class="kv"><span>Opening float</span><strong>KES {{ format(openSession.opening_float) }}</strong></div>
          <div class="kv"><span>Cash sales so far</span><strong>KES {{ format(openSession.cash_sales) }}</strong></div>
          <div class="kv"><span>Cash drops</span><strong>KES {{ format(openSession.cash_drops) }}</strong></div>
          <div class="kv"><span>Expected in drawer</span><strong>KES {{ format(expected) }}</strong></div>
          <label>Counted amount in drawer (to close)</label>
          <input type="number" v-model.number="countedClose" style="max-width:200px" />
          <button class="primary" @click="closeTill" :disabled="closing" style="margin-left:8px">
            {{ closing ? 'Closing…' : 'Close cash session' }}
          </button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
        <p class="ok"  v-if="ok">{{ ok }}</p>
      </div>

      <div class="card">
        <h3>Shops &amp; locations</h3>
        <p class="muted small">Add extra branches to enable multi-location stock and stock transfers.</p>
        <table v-if="shops.length" style="margin:12px 0;">
          <thead><tr><th>Name</th><th>Code</th><th>City</th><th>Warehouse?</th><th>Active</th><th></th></tr></thead>
          <tbody>
            <tr v-for="sh in shops" :key="sh.id">
              <td><strong>{{ sh.name }}</strong></td>
              <td class="muted">{{ sh.code || '—' }}</td>
              <td>{{ sh.city || '—' }}</td>
              <td>{{ sh.is_warehouse ? 'Yes' : '' }}</td>
              <td>{{ sh.is_active ? '✓' : '—' }}</td>
              <td>
                <button class="secondary small" @click="openShopEdit(sh)" v-if="userRole === 'owner'">Edit</button>
                <button class="secondary small" @click="toggleShop(sh)" style="margin-left:6px" v-if="userRole === 'owner'">
                  {{ sh.is_active ? 'Deactivate' : 'Activate' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <button class="primary" @click="openShopNew" v-if="userRole === 'owner'">+ Add shop</button>
      </div>

      <div class="card">
        <h3>Service providers</h3>
        <p class="muted small">Mark staff as providers so they show up on the appointments calendar. Set a commission % on services they perform.</p>
        <table v-if="staff.length" style="margin: 12px 0;">
          <thead><tr><th>Name</th><th>Role</th><th class="r">Commission %</th><th>Provider?</th><th></th></tr></thead>
          <tbody>
            <tr v-for="u in staff" :key="u.id">
              <td><strong>{{ u.name }}</strong></td>
              <td>{{ u.role }}</td>
              <td class="r">
                <input type="number" v-model.number="u.default_commission_percent" min="0" max="100" step="0.5" style="width:70px; padding:4px; text-align:right" />
              </td>
              <td>
                <label style="text-transform:none; letter-spacing:0; font-weight:500;">
                  <input type="checkbox" v-model="u.is_service_provider" :true-value="1" :false-value="0" style="width:auto; margin-right:6px" />
                  {{ u.is_service_provider ? 'Yes' : 'No' }}
                </label>
              </td>
              <td><button class="secondary small" @click="saveProviderRow(u)" :disabled="savingProv === u.id">
                {{ savingProv === u.id ? 'Saving…' : 'Save' }}
              </button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted small">No users yet. Add staff first in the Users section.</p>
      </div>

      <div class="card">
        <h3>Kitchen stations</h3>
        <p class="muted small">Stations route menu items to the right kitchen or bar. Only used in Restaurant mode.</p>
        <table v-if="stations.length" style="margin: 12px 0;">
          <thead><tr><th>Name</th><th>Type</th><th></th></tr></thead>
          <tbody>
            <tr v-for="st in stations" :key="st.id">
              <td><strong>{{ st.name }}</strong></td>
              <td>{{ st.type }}</td>
              <td>
                <button class="secondary small" @click="openStationEdit(st)" v-if="userRole === 'owner' || userRole === 'manager'">Edit</button>
                <button class="danger small" @click="removeStation(st)" v-if="userRole === 'owner'" style="margin-left:6px">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
        <button class="primary" @click="openStationNew" v-if="userRole === 'owner' || userRole === 'manager'">+ Add station</button>
      </div>

      <div class="card">
        <h3>SMS receipts</h3>
        <p class="muted small">Send an SMS receipt to the customer's phone on every sale. Uses your Africa's Talking account.</p>
        <div class="row-2">
          <div><label>Africa's Talking username</label><input v-model="smsForm.atUsername" /></div>
          <div><label>Sender ID (optional)</label><input v-model="smsForm.atSenderId" placeholder="e.g. PAYSOL" /></div>
        </div>
        <label>API key</label>
        <input v-model="smsForm.atApiKey" type="password" :placeholder="smsForm.api_key_set ? '•••• saved — leave blank to keep' : ''" />
        <label style="text-transform:none; letter-spacing:0; font-weight:500;">
          <input type="checkbox" v-model="smsReceiptsEnabled" style="width:auto; margin-right:6px" />
          Automatically send SMS receipts on completed sales (customer phone must be attached).
        </label>
        <label>SMS template</label>
        <input v-model="smsForm.smsReceiptTemplate" />
        <p class="muted small">Tokens: <code>{name}</code> <code>{total}</code> <code>{shop}</code> <code>{receipt}</code> <code>{business}</code></p>
        <div class="row-2" style="margin-top:12px">
          <button class="secondary" @click="saveSmsSettings" :disabled="smsSaving">{{ smsSaving ? 'Saving…' : 'Save SMS settings' }}</button>
          <div>
            <input v-model="smsTestPhone" placeholder="Test phone 0712345678" />
            <button class="secondary" @click="sendTestSms" :disabled="smsTesting || !smsTestPhone" style="margin-top:6px">
              {{ smsTesting ? 'Sending…' : 'Send test SMS' }}
            </button>
          </div>
        </div>
        <p v-if="smsMsg" :class="smsMsgClass">{{ smsMsg }}</p>

        <div style="margin-top:16px; padding-top:14px; border-top:1px dashed var(--paysol-line);">
          <strong style="color:var(--paysol-text);">End-of-day digest</strong>
          <p class="muted small">Send today's numbers as an SMS to your phone. Great for a quick evening review.</p>
          <button class="secondary" @click="sendDigest" :disabled="digestSending">
            {{ digestSending ? 'Sending…' : 'Send end-of-day digest now' }}
          </button>
          <p v-if="digestMsg" :class="digestOk ? 'ok' : 'err'" style="margin-top:8px;">{{ digestMsg }}</p>
          <pre v-if="digestPreview" class="digest-preview">{{ digestPreview }}</pre>
        </div>
      </div>

      <div class="card" v-if="mySub">
        <h3>Your Paysol subscription</h3>
        <div class="sub-summary">
          <div><span>Plan</span><strong>{{ mySub.subscription_plan }}</strong></div>
          <div><span>Amount</span><strong>KES {{ format(mySub.subscription_amount) }}/mo</strong></div>
          <div><span>Status</span><strong :class="subStatusClass">{{ mySub.subscription_status }}</strong></div>
          <div><span>Next due</span><strong>{{ mySub.subscription_next_due || '—' }}</strong></div>
        </div>
        <p v-if="myOutstanding > 0" class="warn small" style="margin-top:8px;">
          You have KES {{ format(myOutstanding) }} outstanding on {{ myInvoices.filter(i => i.status === 'pending').length }} invoice(s).
        </p>

        <table v-if="myInvoices.length" style="margin-top: 12px;">
          <thead><tr><th>Invoice</th><th>Period</th><th class="r">Amount</th><th>Status</th><th>Paid</th><th></th></tr></thead>
          <tbody>
            <tr v-for="inv in myInvoices" :key="inv.id">
              <td><code>{{ inv.invoice_no }}</code></td>
              <td class="muted small">{{ inv.period_start }} → {{ inv.period_end }}</td>
              <td class="r"><strong>KES {{ format(inv.amount) }}</strong></td>
              <td><span :class="'my-inv-pill ' + inv.status">{{ inv.status }}</span></td>
              <td class="muted small">{{ inv.paid_at || (inv.status === 'void' ? 'VOIDED' : '—') }}</td>
              <td>
                <button v-if="inv.status === 'pending'" class="primary small" @click="openPayInvoice(inv)">
                  <i class="mdi mdi-cellphone-wireless"></i> Pay via M-Pesa
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted small" style="margin-top: 8px;">No invoices issued yet.</p>
        <p class="muted small" style="margin-top: 12px;">
          Questions about your bill? Email <a href="mailto:hello@paysol.africa">hello@paysol.africa</a>.
        </p>
      </div>

      <div class="card">
        <h3>Loyalty program</h3>
        <p class="muted small">Reward customers automatically. Points earned on completed sales, redeemable as discount at the till.</p>
        <label style="text-transform:none; letter-spacing:0; font-weight:500;">
          <input type="checkbox" v-model="loyalty.loyalty_enabled" :true-value="1" :false-value="0" style="width:auto; margin-right:6px" />
          Enable loyalty for {{ tenantName }}
        </label>
        <div class="row-2" v-if="loyalty.loyalty_enabled">
          <div>
            <label>Earn rate (points per KES 100 spent)</label>
            <input type="number" v-model.number="loyalty.loyalty_earn_rate" min="0" step="0.5" />
            <p class="muted small">e.g. 1.0 = 1 point per KES 100. A sale of KES 1,000 earns 10 points.</p>
          </div>
          <div>
            <label>Redeem value (KES per point)</label>
            <input type="number" v-model.number="loyalty.loyalty_redeem_rate" min="0" step="0.1" />
            <p class="muted small">e.g. 1.0 = 1 point worth KES 1 at the till. 100 points → KES 100 off.</p>
          </div>
        </div>
        <div class="row-2" v-if="loyalty.loyalty_enabled">
          <div>
            <label>Points expire after (days)</label>
            <input type="number" v-model.number="loyalty.loyalty_expiry_days" min="0" step="30" />
            <p class="muted small">0 = never expire. Common: 365. A daily job zeroes points older than this.</p>
          </div>
        </div>
        <div v-if="loyalty.loyalty_enabled" class="loyalty-preview">
          <strong>Preview:</strong> a KES 1,000 sale earns
          <span class="hilite">{{ Math.floor(1000/100 * (Number(loyalty.loyalty_earn_rate)||0)) }} points</span>,
          which redeem for
          <span class="hilite">KES {{ format(Math.floor(1000/100 * (Number(loyalty.loyalty_earn_rate)||0)) * (Number(loyalty.loyalty_redeem_rate)||0)) }}</span>
          later — that's <strong>{{ effLoyRate(loyalty) }}%</strong> back to the customer.
        </div>
        <div style="margin-top:12px">
          <button class="primary" @click="saveLoyalty" :disabled="savingLoy">{{ savingLoy ? 'Saving…' : 'Save loyalty settings' }}</button>
        </div>
        <p v-if="loyMsg" :class="loyOk ? 'ok' : 'err'" style="margin-top:8px">{{ loyMsg }}</p>

        <div v-if="loyalty.loyalty_enabled" style="margin-top:20px; padding-top:16px; border-top:1px solid var(--paysol-line)">
          <h4 style="margin:0 0 4px">Loyalty tiers <span class="muted small" style="font-weight:400">(optional)</span></h4>
          <p class="muted small">Reward regulars with bonus earn rates once they cross annual spend thresholds. Sorted by threshold, ascending.</p>
          <table class="tier-table" v-if="tiers.length">
            <thead>
              <tr>
                <th>Name</th>
                <th>Colour</th>
                <th>Min spend / year (KES)</th>
                <th>Earn ×</th>
                <th>Perks note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(t, i) in tiers" :key="i">
                <td><input v-model="t.name" placeholder="e.g. Silver" maxlength="60" /></td>
                <td><input type="color" v-model="t.color" style="width:40px; padding:0; height:32px" /></td>
                <td><input type="number" v-model.number="t.min_spend_ytd" min="0" step="1000" /></td>
                <td><input type="number" v-model.number="t.earn_multiplier" min="0.5" max="5" step="0.25" /></td>
                <td><input v-model="t.perks_note" placeholder="e.g. Priority seating" maxlength="255" /></td>
                <td><button class="danger small" @click="removeTier(i)" title="Remove tier">&times;</button></td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted small">No tiers yet — every customer gets the base earn rate.</p>
          <div style="margin-top:10px; display:flex; gap:8px">
            <button class="ghost" @click="addTier"><i class="mdi mdi-plus"></i> Add tier</button>
            <button class="primary" @click="saveTiers" :disabled="savingTiers">
              {{ savingTiers ? 'Saving…' : 'Save tiers' }}
            </button>
          </div>
          <p v-if="tierMsg" :class="tierOk ? 'ok' : 'err'" style="margin-top:8px">{{ tierMsg }}</p>
        </div>
      </div>

      <div class="card">
        <h3>Receipt customisation</h3>
        <p class="muted small">Text shown on printed and digital receipts. Keep it short — receipts are narrow.</p>
        <label>Header line (shown just under the business name)</label>
        <input v-model="rcpt.receipt_header" placeholder="e.g. Serving Nairobi since 2020" maxlength="255" />
        <label>Footer message</label>
        <input v-model="rcpt.receipt_footer" placeholder="e.g. Thank you! No refunds after 7 days." maxlength="500" />
        <div class="row-2">
          <label style="text-transform:none; letter-spacing:0; font-weight:500;">
            <input type="checkbox" v-model="rcpt.receipt_show_qr" :true-value="1" :false-value="0" style="width:auto; margin-right:6px" />
            Show QR code on printed receipt (customer scans to see digital copy)
          </label>
          <label style="text-transform:none; letter-spacing:0; font-weight:500;">
            <input type="checkbox" v-model="rcpt.receipt_show_customer_link" :true-value="1" :false-value="0" style="width:auto; margin-right:6px" />
            Show digital-receipt URL on printed receipt
          </label>
        </div>
        <div style="margin-top:12px">
          <button class="primary" @click="saveRcpt" :disabled="savingRcpt">{{ savingRcpt ? 'Saving…' : 'Save receipt settings' }}</button>
        </div>
        <p v-if="rcptMsg" :class="rcptOk ? 'ok' : 'err'" style="margin-top:8px">{{ rcptMsg }}</p>
      </div>

      <div class="card" v-if="bookingLink">
        <h3>Public booking link</h3>
        <p class="muted small">Share this link with customers so they can book appointments themselves. Works for salons/spas/clinics.</p>
        <div class="link-row">
          <input :value="bookingLink" readonly ref="linkInput" @focus="$event.target.select()" />
          <button class="secondary" @click="copyLink">
            <i class="mdi mdi-content-copy"></i> Copy
          </button>
          <a :href="bookingLink" target="_blank" class="secondary link-btn">
            <i class="mdi mdi-open-in-new"></i> Open
          </a>
        </div>
      </div>

      <div class="card">
        <h3>M-Pesa paybills</h3>
        <p class="muted small">Add your Daraja credentials so Paysol can send STK push prompts from your POS.
        You need one paybill / till configured before the "M-Pesa STK" button on the POS works live.</p>

        <table v-if="paybills.length" style="margin: 12px 0;">
          <thead><tr><th>Shortcode</th><th>Type</th><th>Environment</th><th>Primary</th><th>Platform</th><th></th></tr></thead>
          <tbody>
            <tr v-for="p in paybills" :key="p.id">
              <td><strong>{{ p.shortcode }}</strong></td>
              <td>{{ p.type }}</td>
              <td>{{ p.environment }}</td>
              <td>{{ p.is_primary ? '★' : '' }}</td>
              <td>
                <span v-if="p.is_platform_paybill" title="This paybill collects Paysol subscriptions">
                  <i class="mdi mdi-crown" style="color: var(--paysol-orange);"></i>
                </span>
              </td>
              <td>
                <button class="secondary small" @click="testPaybill(p.id)" :disabled="testing===p.id">
                  {{ testing === p.id ? 'Testing…' : 'Test connection' }}
                </button>
                <button v-if="isPlatformOwner && !p.is_platform_paybill" class="secondary small" style="margin-left:6px" @click="setAsPlatformPaybill(p)">
                  Make Paysol collections
                </button>
                <button class="danger small" style="margin-left:6px" @click="removePaybill(p.id)">Remove</button>
              </td>
            </tr>
          </tbody>
        </table>

        <button class="primary" @click="showPaybillForm=true" v-if="userRole === 'owner'">
          + Add paybill
        </button>
        <p v-if="paybillMsg" :class="paybillMsgClass">{{ paybillMsg }}</p>
      </div>

      <div class="card">
        <h3>API</h3>
        <div class="kv"><span>API base</span><strong>{{ apiBase }}</strong></div>
        <p class="muted small">The portal is served from <strong>https://www.paysol.africa/</strong>. The API is served from <strong>https://api.paysol.africa/paysol/</strong>. Change in <code>.env</code>.</p>
      </div>
    </div>

    <div v-if="payInv" class="modal-back" @click.self="closePayInvoice">
      <div class="modal card">
        <h3>Pay {{ payInv.invoice_no }}</h3>
        <div class="ck-total">Amount: <strong>KES {{ format(payInv.amount) }}</strong></div>
        <div v-if="payStkStatus === null">
          <label>M-Pesa phone (STK prompt sent here)</label>
          <input v-model="payPhone" placeholder="0712345678" />
          <p class="muted small">You'll see a prompt on your phone. Enter your M-Pesa PIN to confirm.</p>
          <div class="modal-actions">
            <button class="secondary" @click="closePayInvoice">Cancel</button>
            <button class="primary" @click="sendPayStk" :disabled="!payPhone || sendingStk">
              {{ sendingStk ? 'Sending…' : 'Send STK prompt' }}
            </button>
          </div>
          <p v-if="payErr" class="err">{{ payErr }}</p>
        </div>
        <div v-else class="stk-status">
          <div class="stk-spinner" v-if="payStkStatus === 'pending'"><i class="mdi mdi-cellphone-wireless"></i></div>
          <div class="stk-done" v-else-if="payStkStatus === 'paid'"><i class="mdi mdi-check-circle"></i></div>
          <div class="stk-fail" v-else><i class="mdi mdi-close-circle"></i></div>
          <p><strong>{{ stkStatusText }}</strong></p>
          <p v-if="payMpesaReceipt" class="muted small">M-Pesa receipt: {{ payMpesaReceipt }}</p>
          <div class="modal-actions" v-if="payStkStatus !== 'pending'">
            <button class="primary" @click="closePayInvoice">Close</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showStationForm" class="modal-back" @click.self="showStationForm=false">
      <div class="modal card">
        <h3>{{ stationForm.id ? 'Edit station' : 'Add station' }}</h3>
        <label>Name</label><input v-model="stationForm.name" placeholder="e.g. Grill" />
        <label>Type</label>
        <select v-model="stationForm.type">
          <option value="kitchen">Kitchen</option>
          <option value="bar">Bar</option>
          <option value="grill">Grill</option>
          <option value="bakery">Bakery</option>
          <option value="general">General</option>
        </select>
        <div class="modal-actions">
          <button class="secondary" @click="showStationForm=false">Cancel</button>
          <button class="primary" @click="saveStation" :disabled="savingStation">{{ savingStation ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <div v-if="showShopForm" class="modal-back" @click.self="showShopForm=false">
      <div class="modal card">
        <h3>{{ shopForm.id ? 'Edit shop' : 'Add shop' }}</h3>
        <label>Name</label><input v-model="shopForm.name" />
        <div class="row-2">
          <div><label>Code</label><input v-model="shopForm.code" placeholder="e.g. WEST" /></div>
          <div><label>City</label><input v-model="shopForm.city" /></div>
        </div>
        <label>Address</label><input v-model="shopForm.address" />
        <label>Phone</label><input v-model="shopForm.phone" />
        <label style="text-transform:none; letter-spacing:0; font-weight:500;">
          <input type="checkbox" v-model="shopForm.isWarehouse" style="width:auto; margin-right:6px" />
          This location is a warehouse (not a retail outlet)
        </label>
        <div class="modal-actions">
          <button class="secondary" @click="showShopForm=false">Cancel</button>
          <button class="primary" @click="saveShop" :disabled="savingShop">{{ savingShop ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <div v-if="showPaybillForm" class="modal-back" @click.self="showPaybillForm=false">
      <div class="modal card">
        <h3>Add M-Pesa paybill</h3>
        <label>Shortcode</label>
        <input v-model="paybillForm.shortcode" placeholder="e.g. 174379" />
        <label>Type</label>
        <select v-model="paybillForm.type">
          <option value="paybill">Paybill</option>
          <option value="till">Till (Buy Goods)</option>
        </select>
        <label>Consumer Key</label>
        <input v-model="paybillForm.consumerKey" />
        <label>Consumer Secret</label>
        <input v-model="paybillForm.consumerSecret" type="password" />
        <label>Passkey</label>
        <input v-model="paybillForm.passkey" type="password" />
        <label>Environment</label>
        <select v-model="paybillForm.environment">
          <option value="sandbox">Sandbox (testing)</option>
          <option value="production">Production (live)</option>
        </select>
        <label style="text-transform:none; letter-spacing:0; font-weight:500;">
          <input type="checkbox" v-model="paybillForm.isPrimary" style="width:auto; margin-right:6px" />
          Make this the primary paybill (used by POS)
        </label>
        <div class="modal-actions">
          <button class="secondary" @click="showPaybillForm=false">Cancel</button>
          <button class="primary" @click="savePaybill" :disabled="saving">
            {{ saving ? 'Saving…' : 'Save paybill' }}
          </button>
        </div>
        <p class="err" v-if="paybillErr">{{ paybillErr }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'SettingsPage',
  components: { Navigation },
  data() {
    return {
      tenantId: sessionStorage.getItem('tenantId'),
      tenantName: sessionStorage.getItem('tenantName'),
      userName: sessionStorage.getItem('userName'),
      userRole: sessionStorage.getItem('userRole'),
      apiBase: paysol,
      openSession: null,
      openingFloat: 0, countedClose: 0,
      opening: false, closing: false,
      err: '', ok: '',
      paybills: [], testing: 0,
      showPaybillForm: false, saving: false, paybillErr: '',
      paybillMsg: '', paybillMsgClass: 'muted small',
      paybillForm: { shortcode: '', type: 'paybill', consumerKey: '', consumerSecret: '', passkey: '', environment: 'sandbox', isPrimary: 1 },
      // Shops
      shops: [], showShopForm: false, savingShop: false,
      shopForm: { id: 0, name: '', code: '', city: '', address: '', phone: '', isWarehouse: 0 },
      // Stations
      stations: [], showStationForm: false, savingStation: false,
      stationForm: { id: 0, name: '', type: 'kitchen' },
      // Staff providers
      staff: [], savingProv: 0,
      // Digest + booking link
      digestSending: false, digestMsg: '', digestOk: false, digestPreview: '',
      publicSlug: null,
      // My subscription
      mySub: null, myInvoices: [], myOutstanding: 0,
      // Receipt
      rcpt: { receipt_header: '', receipt_footer: '', receipt_show_qr: 1, receipt_show_customer_link: 1 },
      savingRcpt: false, rcptMsg: '', rcptOk: false,
      // Loyalty
      loyalty: { loyalty_enabled: 0, loyalty_earn_rate: 1, loyalty_redeem_rate: 1, loyalty_expiry_days: 0 },
      savingLoy: false, loyMsg: '', loyOk: false,
      tiers: [], savingTiers: false, tierMsg: '', tierOk: false,
      isPlatformOwner: sessionStorage.getItem('isPlatformOwner') === '1',
      // Pay STK
      payInv: null, payPhone: '', sendingStk: false, payErr: '',
      payStkId: null, payStkStatus: null, payMpesaReceipt: '',
      payPollHandle: null, payPollDeadline: 0,
      // SMS
      smsForm: { atUsername: '', atApiKey: '', atSenderId: '', smsReceiptTemplate: '', api_key_set: false },
      smsReceiptsEnabled: false,
      smsSaving: false, smsTesting: false, smsTestPhone: '',
      smsMsg: '', smsMsgClass: 'muted small',
    };
  },
  computed: {
    expected() {
      if (!this.openSession) return 0;
      return parseFloat(this.openSession.opening_float)
           + parseFloat(this.openSession.cash_sales)
           - parseFloat(this.openSession.cash_refunds)
           - parseFloat(this.openSession.cash_drops);
    },
    bookingLink() {
      if (!this.publicSlug) return '';
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.paysol.africa';
      return `${origin}/#/book/${this.publicSlug}`;
    },
    subStatusClass() {
      const s = this.mySub && this.mySub.subscription_status;
      if (s === 'active') return 'ok';
      if (s === 'past_due') return 'warn';
      if (s === 'cancelled') return 'err';
      return '';
    },
    stkStatusText() {
      if (this.payStkStatus === 'pending') return 'Waiting for you to enter your PIN on the phone…';
      if (this.payStkStatus === 'paid')    return 'Payment received. Invoice marked paid.';
      if (this.payStkStatus === 'cancelled') return 'You cancelled the M-Pesa prompt.';
      if (this.payStkStatus === 'failed')    return 'Payment failed. Try again or use another method.';
      if (this.payStkStatus === 'timeout')   return 'Timed out waiting for payment.';
      return '';
    },
  },
  async mounted() {
    await Promise.all([this.loadSession(), this.loadPaybills(), this.loadShops(), this.loadSmsSettings(), this.loadStations(), this.loadStaff(), this.loadMe(), this.loadMyInvoices(), this.loadReceiptSettings(), this.loadLoyalty()]);
  },
  beforeUnmount() {
    if (this.payPollHandle) clearInterval(this.payPollHandle);
  },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async loadPaybills() {
      try {
        const { data } = await execute({ function: 'listPaybills' }, paysol);
        if (data.success) this.paybills = data.data;
      } catch (e) { /* silent */ }
    },
    async savePaybill() {
      this.saving = true; this.paybillErr = '';
      try {
        const { data } = await execute({ function: 'savePaybill', ...this.paybillForm }, paysol);
        if (!data.success) { this.paybillErr = data.message; return; }
        this.showPaybillForm = false;
        this.paybillForm = { shortcode: '', type: 'paybill', consumerKey: '', consumerSecret: '', passkey: '', environment: 'sandbox', isPrimary: 1 };
        await this.loadPaybills();
        this.paybillMsg = 'Paybill added. Tap "Test connection" to verify.';
        this.paybillMsgClass = 'ok';
      } catch (e) { this.paybillErr = e.response?.data?.message || 'Save failed.'; }
      finally { this.saving = false; }
    },
    async removePaybill(id) {
      if (!confirm('Remove this paybill?')) return;
      const { data } = await execute({ function: 'deletePaybill', id }, paysol);
      if (data.success) { await this.loadPaybills(); this.paybillMsg = 'Paybill removed.'; this.paybillMsgClass = 'muted small'; }
    },
    async loadMe() {
      try {
        const { data } = await execute({ function: 'getMe' }, paysol);
        if (data.success && data.data.tenant) this.publicSlug = data.data.tenant.publicSlug;
      } catch (e) { /* silent */ }
    },
    async setAsPlatformPaybill(p) {
      if (!confirm(`Make paybill ${p.shortcode} the Paysol collections paybill? All tenants will pay their subscriptions to this account.`)) return;
      const { data } = await execute({ function: 'setPlatformPaybill', id: p.id }, paysol);
      if (data.success) { alert(data.message); await this.loadPaybills(); }
      else alert(data.message);
    },
    openPayInvoice(inv) {
      this.payInv = inv;
      this.payPhone = ''; this.payErr = '';
      this.payStkId = null; this.payStkStatus = null; this.payMpesaReceipt = '';
    },
    closePayInvoice() {
      if (this.payPollHandle) { clearInterval(this.payPollHandle); this.payPollHandle = null; }
      this.payInv = null;
      // Refresh invoices after closing so paid ones update
      this.loadMyInvoices();
    },
    async sendPayStk() {
      this.sendingStk = true; this.payErr = '';
      try {
        const { data } = await execute({ function: 'payPlatformInvoiceStk', invoiceId: this.payInv.id, phone: this.payPhone }, paysol);
        if (!data.success) { this.payErr = data.message; return; }
        this.payStkId = data.data.stkId;
        this.payStkStatus = 'pending';
        this.startPolling();
      } finally { this.sendingStk = false; }
    },
    startPolling() {
      this.payPollDeadline = Date.now() + 90000;   // 90 s
      this.payPollHandle = setInterval(async () => {
        if (Date.now() > this.payPollDeadline) {
          clearInterval(this.payPollHandle); this.payPollHandle = null;
          this.payStkStatus = 'timeout';
          return;
        }
        try {
          const { data } = await execute({ function: 'checkPlatformInvoicePayment', stkId: this.payStkId }, paysol);
          if (!data.success) return;
          if (data.data.invoiceStatus === 'paid') {
            clearInterval(this.payPollHandle); this.payPollHandle = null;
            this.payStkStatus = 'paid';
            this.payMpesaReceipt = data.data.mpesaReceipt || '';
          } else if (['cancelled','failed'].includes(data.data.stkStatus)) {
            clearInterval(this.payPollHandle); this.payPollHandle = null;
            this.payStkStatus = data.data.stkStatus;
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    },
    effLoyRate(l) {
      const earn = Number(l.loyalty_earn_rate) || 0;
      const redeem = Number(l.loyalty_redeem_rate) || 0;
      return ((earn * redeem)).toFixed(2);   // e.g. 1 point per 100 * 1 KES/point = 1% back
    },
    async loadLoyalty() {
      try {
        const { data } = await execute({ function: 'getLoyaltySettings' }, paysol);
        if (data.success && data.data) {
          const s = data.data.settings || {};
          this.loyalty = {
            loyalty_enabled: Number(s.loyalty_enabled) || 0,
            loyalty_earn_rate: Number(s.loyalty_earn_rate),
            loyalty_redeem_rate: Number(s.loyalty_redeem_rate),
            loyalty_expiry_days: Number(s.loyalty_expiry_days) || 0,
          };
          this.tiers = (data.data.tiers || []).map(t => ({
            id: Number(t.id),
            name: t.name,
            min_spend_ytd: Number(t.min_spend_ytd),
            earn_multiplier: Number(t.earn_multiplier),
            perks_note: t.perks_note || '',
            color: t.color || '#9E9E9E',
            sort_order: Number(t.sort_order) || 0,
          }));
        }
      } catch (e) { /* silent */ }
    },
    async saveLoyalty() {
      this.savingLoy = true; this.loyMsg = '';
      try {
        const { data } = await execute({
          function: 'saveLoyaltySettings',
          loyaltyEnabled: this.loyalty.loyalty_enabled ? 1 : 0,
          loyaltyEarnRate: this.loyalty.loyalty_earn_rate,
          loyaltyRedeemRate: this.loyalty.loyalty_redeem_rate,
          loyaltyExpiryDays: this.loyalty.loyalty_expiry_days || 0,
        }, paysol);
        this.loyOk = data.success; this.loyMsg = data.success ? 'Saved.' : ('Failed: ' + data.message);
      } finally { this.savingLoy = false; }
    },
    addTier() {
      this.tiers.push({
        name: '', min_spend_ytd: 0, earn_multiplier: 1.0,
        perks_note: '', color: '#9E9E9E', sort_order: this.tiers.length + 1,
      });
    },
    removeTier(i) { this.tiers.splice(i, 1); },
    async saveTiers() {
      this.savingTiers = true; this.tierMsg = '';
      try {
        const payload = this.tiers
          .filter(t => (t.name || '').trim())
          .map((t, i) => ({ ...t, sort_order: i + 1 }));
        const { data } = await execute({ function: 'saveLoyaltyTiers', tiers: payload }, paysol);
        this.tierOk = data.success;
        this.tierMsg = data.success ? 'Tiers saved. All customers re-tiered.' : ('Failed: ' + data.message);
        if (data.success) await this.loadLoyalty();
      } finally { this.savingTiers = false; }
    },
    async loadReceiptSettings() {
      try {
        const { data } = await execute({ function: 'getReceiptSettings' }, paysol);
        if (data.success && data.data) {
          this.rcpt = {
            receipt_header: data.data.receipt_header || '',
            receipt_footer: data.data.receipt_footer || '',
            receipt_show_qr: Number(data.data.receipt_show_qr) || 0,
            receipt_show_customer_link: Number(data.data.receipt_show_customer_link) || 0,
          };
        }
      } catch (e) { /* silent */ }
    },
    async saveRcpt() {
      this.savingRcpt = true; this.rcptMsg = '';
      try {
        const { data } = await execute({ function: 'saveReceiptSettings',
          receiptHeader: this.rcpt.receipt_header,
          receiptFooter: this.rcpt.receipt_footer,
          receiptShowQr: this.rcpt.receipt_show_qr ? 1 : 0,
          receiptShowCustomerLink: this.rcpt.receipt_show_customer_link ? 1 : 0,
        }, paysol);
        this.rcptOk = data.success; this.rcptMsg = data.success ? 'Saved.' : ('Failed: ' + data.message);
      } finally { this.savingRcpt = false; }
    },
    async loadMyInvoices() {
      try {
        const { data } = await execute({ function: 'listMyPlatformInvoices' }, paysol);
        if (data.success) {
          this.mySub = data.data.subscription;
          this.myInvoices = data.data.invoices;
          this.myOutstanding = Number(data.data.outstanding) || 0;
        }
      } catch (e) { /* silent */ }
    },
    async sendDigest() {
      this.digestSending = true; this.digestMsg = '';
      try {
        const { data } = await execute({ function: 'sendEndOfDayDigest' }, paysol);
        this.digestOk = data.success;
        this.digestMsg = data.success ? ('Sent to ' + data.data.sent_to) : ('Failed: ' + data.message);
        if (data.success) this.digestPreview = data.data.preview;
      } catch (e) {
        this.digestOk = false; this.digestMsg = 'Failed: ' + (e.response?.data?.message || 'Network error.');
      } finally { this.digestSending = false; }
    },
    copyLink() {
      const el = this.$refs.linkInput;
      if (el) { el.select(); document.execCommand('copy'); }
    },
    // ---- Providers ----
    async loadStaff() {
      try {
        const { data } = await execute({ function: 'getUsers' }, paysol);
        if (data.success) this.staff = data.data.map(u => ({ ...u,
          is_service_provider: Number(u.is_service_provider) || 0,
          default_commission_percent: Number(u.default_commission_percent) || 0,
        }));
      } catch (e) { /* silent */ }
    },
    async saveProviderRow(u) {
      this.savingProv = u.id;
      try {
        const { data } = await execute({
          function: 'setProvider', userId: u.id,
          isServiceProvider: u.is_service_provider ? 1 : 0,
          defaultCommissionPercent: u.default_commission_percent,
        }, paysol);
        if (!data.success) alert(data.message);
      } finally { this.savingProv = 0; }
    },
    // ---- Stations ----
    async loadStations() {
      try {
        const { data } = await execute({ function: 'listStations' }, paysol);
        if (data.success) this.stations = data.data;
      } catch (e) { /* silent */ }
    },
    openStationNew() {
      this.stationForm = { id: 0, name: '', type: 'kitchen' };
      this.showStationForm = true;
    },
    openStationEdit(st) {
      this.stationForm = { id: st.id, name: st.name, type: st.type };
      this.showStationForm = true;
    },
    async saveStation() {
      this.savingStation = true;
      try {
        const { data } = await execute({ function: 'saveStation', ...this.stationForm }, paysol);
        if (!data.success) alert(data.message);
        else { this.showStationForm = false; await this.loadStations(); }
      } finally { this.savingStation = false; }
    },
    async removeStation(st) {
      if (!confirm(`Delete station "${st.name}"?`)) return;
      const { data } = await execute({ function: 'deleteStation', id: st.id }, paysol);
      if (data.success) await this.loadStations();
      else alert(data.message);
    },
    // ---- Shops ----
    async loadShops() {
      try {
        const { data } = await execute({ function: 'listShops' }, paysol);
        if (data.success) this.shops = data.data;
      } catch (e) { /* silent */ }
    },
    openShopNew() {
      this.shopForm = { id: 0, name: '', code: '', city: '', address: '', phone: '', isWarehouse: 0 };
      this.showShopForm = true;
    },
    openShopEdit(sh) {
      this.shopForm = { id: sh.id, name: sh.name || '', code: sh.code || '', city: sh.city || '', address: sh.address || '', phone: sh.phone || '', isWarehouse: sh.is_warehouse ? 1 : 0 };
      this.showShopForm = true;
    },
    async saveShop() {
      this.savingShop = true;
      try {
        const { data } = await execute({ function: 'saveShop', ...this.shopForm }, paysol);
        if (!data.success) alert(data.message);
        else { this.showShopForm = false; await this.loadShops(); }
      } finally { this.savingShop = false; }
    },
    async toggleShop(sh) {
      const { data } = await execute({ function: 'toggleShopActive', id: sh.id }, paysol);
      if (data.success) await this.loadShops();
    },
    // ---- SMS ----
    async loadSmsSettings() {
      try {
        const { data } = await execute({ function: 'getSmsSettings' }, paysol);
        if (!data.success) return;
        this.smsForm.atUsername = data.data.at_username || '';
        this.smsForm.atSenderId = data.data.at_sender_id || '';
        this.smsForm.smsReceiptTemplate = data.data.sms_receipt_template || '';
        this.smsForm.api_key_set = !!data.data.api_key_set;
        this.smsReceiptsEnabled = !!Number(data.data.sms_receipts_enabled);
        this.smsForm.atApiKey = '';
      } catch (e) { /* silent */ }
    },
    async saveSmsSettings() {
      this.smsSaving = true; this.smsMsg = '';
      try {
        const { data } = await execute({
          function: 'saveSmsSettings',
          atUsername: this.smsForm.atUsername,
          atApiKey: this.smsForm.atApiKey,
          atSenderId: this.smsForm.atSenderId,
          smsReceiptsEnabled: this.smsReceiptsEnabled ? 1 : 0,
          smsReceiptTemplate: this.smsForm.smsReceiptTemplate,
        }, paysol);
        this.smsMsg = data.success ? 'Saved.' : ('Failed: ' + data.message);
        this.smsMsgClass = data.success ? 'ok' : 'err';
        if (data.success) { this.smsForm.atApiKey = ''; await this.loadSmsSettings(); }
      } finally { this.smsSaving = false; }
    },
    async sendTestSms() {
      this.smsTesting = true; this.smsMsg = '';
      try {
        const { data } = await execute({ function: 'sendTestSms', phone: this.smsTestPhone }, paysol);
        this.smsMsg = data.success ? ('Test SMS sent · ref: ' + (data.data?.ref || '—')) : ('Failed: ' + data.message);
        this.smsMsgClass = data.success ? 'ok' : 'err';
      } catch (e) {
        this.smsMsg = 'Failed: ' + (e.response?.data?.message || 'Network error.');
        this.smsMsgClass = 'err';
      }
      finally { this.smsTesting = false; }
    },
    async testPaybill(id) {
      this.testing = id; this.paybillMsg = '';
      try {
        const { data } = await execute({ function: 'testPaybillConnection', id }, paysol);
        this.paybillMsg = data.success ? ('Connection OK · Token: ' + (data.data?.token_snippet || '—')) : ('Failed: ' + data.message);
        this.paybillMsgClass = data.success ? 'ok' : 'err';
      } catch (e) {
        this.paybillMsg = 'Failed: ' + (e.response?.data?.message || 'Network error.');
        this.paybillMsgClass = 'err';
      }
      finally { this.testing = 0; }
    },
    async loadSession() {
      const { data } = await execute({ function: 'getMyOpenCashSession' }, paysol);
      if (data.success) this.openSession = data.data;
    },
    async openTill() {
      this.opening = true; this.err = ''; this.ok = '';
      try {
        const { data } = await execute({ function: 'openCashSession', openingFloat: this.openingFloat }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.ok = 'Cash session opened.';
        await this.loadSession();
      } catch (e) { this.err = e.response?.data?.message || 'Failed.'; }
      finally { this.opening = false; }
    },
    async closeTill() {
      this.closing = true; this.err = ''; this.ok = '';
      try {
        const { data } = await execute({ function: 'closeCashSession', id: this.openSession.id, countedClose: this.countedClose }, paysol);
        if (!data.success) { this.err = data.message; return; }
        const v = data.data.variance;
        this.ok = 'Cash session closed. Variance: KES ' + this.format(v) + (v === 0 ? ' (exact)' : (v > 0 ? ' (over)' : ' (short)'));
        this.openSession = null;
      } catch (e) { this.err = e.response?.data?.message || 'Failed.'; }
      finally { this.closing = false; }
    }
  }
};
</script>

<style scoped>
.wrap { max-width: 1000px; margin: 0 auto; padding: 24px; }
h1 { font-size: 26px; margin: 0 0 4px; }
.lede { color: var(--paysol-muted); margin: 0 0 20px; }
.card { margin-bottom: 16px; }
h3 { color: var(--paysol-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 12px; }
.kv { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--paysol-line); font-size: 13px; }
.kv:last-child { border-bottom: none; }
.kv span { color: var(--paysol-muted); }
.muted { color: var(--paysol-muted); } .small { font-size: 12px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.ok { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
code { background: var(--paysol-bg-elev); padding: 2px 6px; border-radius: 3px; font-size: 11px; }
button.small { padding: 4px 10px; font-size: 11px; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.paybillMsg { font-size: 12px; margin-top: 8px; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: end; }
.ok { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
.r { text-align: right; }
.digest-preview { background: var(--paysol-bg); padding: 10px 12px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 11px; margin-top: 10px; white-space: pre-wrap; color: var(--paysol-muted); }
.link-row { display: flex; gap: 8px; align-items: center; margin-top: 8px; }
.link-row input { flex: 1; }
.link-row .link-btn { display: inline-flex; align-items: center; gap: 4px; padding: 10px 16px; border-radius: 6px; border: 1px solid var(--paysol-line); color: var(--paysol-text); text-decoration: none; font-size: 14px; font-weight: 600; }
.link-row .link-btn:hover { border-color: var(--paysol-teal); color: var(--paysol-teal); }
.sub-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; padding: 12px; background: var(--paysol-bg-elev); border-radius: 6px; }
.sub-summary div { display: flex; flex-direction: column; gap: 3px; }
.sub-summary span { color: var(--paysol-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
.sub-summary strong { color: var(--paysol-text); font-size: 14px; text-transform: capitalize; }
.sub-summary strong.ok { color: var(--paysol-success); }
.sub-summary strong.warn { color: var(--paysol-warning); }
.sub-summary strong.err { color: var(--paysol-danger); }
.warn { color: var(--paysol-warning); }
.my-inv-pill { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.my-inv-pill.pending { background: rgba(245,158,11,.15); color: var(--paysol-warning); }
.my-inv-pill.paid    { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.my-inv-pill.void    { background: rgba(139,164,196,.15); color: var(--paysol-muted); text-decoration: line-through; }
.loyalty-preview { background: var(--paysol-teal-glow); border-left: 3px solid var(--paysol-teal); padding: 10px 12px; border-radius: 4px; margin-top: 10px; color: var(--paysol-text); font-size: 13px; }
.hilite { color: var(--paysol-teal); font-weight: 700; }
.tier-table { width: 100%; margin-top: 8px; font-size: 13px; }
.tier-table th { text-align: left; padding: 6px 4px; font-size: 11px; color: var(--paysol-muted); text-transform: uppercase; letter-spacing: 1px; }
.tier-table td { padding: 4px 4px; }
.tier-table input { width: 100%; }
.danger.small { padding: 4px 8px; font-size: 14px; line-height: 1; }
code { background: var(--paysol-bg-elev); padding: 2px 6px; border-radius: 3px; color: var(--paysol-teal); font-size: 11px; }
.r { text-align: right; }
.ck-total { color: var(--paysol-teal); font-size: 22px; text-align: center; padding: 12px 0 16px; }
.stk-status { text-align: center; padding: 30px 20px; }
.stk-spinner i, .stk-done i, .stk-fail i { font-size: 60px; }
.stk-spinner i { color: var(--paysol-teal); animation: pulse 1.5s infinite; }
.stk-done i    { color: var(--paysol-success); }
.stk-fail i    { color: var(--paysol-danger); }
@keyframes pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
</style>
